import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { AssignmentInfo, ExplainerInfo } from "./api";
import { ExplainerPlayer, WatchTile, explainerLength } from "./Explainer";
import type { MissionController } from "./mission-steps";
import { LessonPlanButton } from "./LessonPlan";
import { useMe } from "./me";
import { AutoRead, Narrate } from "./Narrate";
import { LEVEL_UP_COINS, type MissionStep } from "@buildlab/shared/steps"; // a subpath, never the root
import "./Mission.css";

// Today's mission (decision 60) as steps (Review 17, D45), shown as a full-width "quest bar" under
// the top bar at every screen size (Paul, Oct 13: the helper column needs its room for the chat).
// Left: where the student is ("Step 2 of 5") and the current step in full, with its speaker and,
// for steps the app can't see, a Done button. Right: one pip per step, "All steps" (a panel under
// the bar listing every step), "Skip steps" (Lesson 2 on, R6), the mission's explainers and, for
// teachers, the lesson plan. Steps tick themselves (mission-steps.ts); the current step's panel
// lights up in the workspace (Workspace data-guide). With steps skipped, or for a mission without
// steps, the bar shows the start of the mission and "Read all" opens the whole of it.
//
// Level-ups (decision 101): in lessons that have them, once the steps are done, skipped or the
// project was submitted, the same bar shows "Level up 1 of 3" with the level-up in full, gold-star
// pips instead of the step pips, and "+5 coins!" when one ticks; then "All levels done". "All steps"
// lists them under a "Level up" heading. The bar stays one line tall (Chromebooks, 1366×635).

/** Lessons from 2 on offer "Skip steps" (R6); Lesson 1 and the Demo always show them. */
export const canSkipSteps = (lesson: number) => lesson >= 2;

export function MissionBar({ assignment, mission }: { assignment: AssignmentInfo; mission?: MissionController | null }) {
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState<ExplainerInfo | null>(null);
  const teacher = useMe()?.user.role === "teacher"; // the lesson plan is for teachers only
  const explainers = assignment.explainers ?? [];
  const steps = mission && !mission.skipped ? mission.steps : null;
  const total = steps?.length ?? 0;
  const current = mission?.current ?? null;
  const at = steps && current ? steps.indexOf(current) + 1 : total;
  const finished = !!steps && !current;
  const skippable = !!mission && canSkipSteps(assignment.lesson);
  // Level-ups (decision 101): `lu` is the bar's level-up mode; `luNow` the current one (null when all done).
  const levelUps = mission?.levelUps ?? [];
  const lu = !!mission?.levelUpsOpen;
  const luNow = lu ? mission!.levelUp : null;
  const luDone = lu && !luNow;
  const luAt = luNow ? levelUps.indexOf(luNow) + 1 : levelUps.length;
  const stepCount = finished ? "Mission complete!" : `Step ${at} of ${total}`;
  const luCount = luDone ? "All levels done!" : `Level up ${luAt} of ${levelUps.length}`;
  const count = lu ? luCount : steps ? stepCount : "Today's mission";
  // The step (or level-up) the bar shows, with its speaker and Done.
  const shown: MissionStep | null = lu ? luNow : steps ? current : null;
  const listed = lu || !!steps; // one step at a time, not the mission paragraph
  // What "Read steps to me" reads: the current step, or the whole mission when steps are skipped.
  const readText = listed ? (shown?.text ?? "") : assignment.instructions;
  const readClip = listed ? (shown ? `${assignment.id}/${shown.id}` : undefined) : `${assignment.id}/mission`;

  // "+5 coins!" beside the count for a moment after a level-up pays (decision 101).
  const [coins, setCoins] = useState<number | null>(null);
  const earned = mission?.earned ?? null;
  useEffect(() => {
    if (!earned) return;
    setCoins(earned.coins);
    const t = setTimeout(() => setCoins(null), 3500);
    return () => clearTimeout(t);
  }, [earned]);

  // A short celebration when the last step ticks while the student is here (not on every visit).
  const [celebrate, setCelebrate] = useState(false);
  const wasFinished = useRef(finished);
  const wasLuDone = useRef(luDone);
  useEffect(() => {
    const now = (finished && !wasFinished.current) || (luDone && !wasLuDone.current);
    wasFinished.current = finished;
    wasLuDone.current = luDone;
    if (now) {
      setCelebrate(true);
      const t = setTimeout(() => setCelebrate(false), 2200);
      return () => clearTimeout(t);
    }
  }, [finished, luDone]);

  // The "All steps" panel: Esc or a click outside closes it; focus goes in when it opens and back
  // to the button when Esc closes it.
  const barRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null); // the panel's opener: All steps, or Read all when steps are skipped
  const panelId = useId();
  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || document.querySelector("[aria-modal='true']")) return;
      e.preventDefault(); // so the workspace's Esc (close the tools drawer) waits for the next press
      setOpen(false);
      buttonRef.current?.focus();
    };
    const onDown = (e: PointerEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [open]);

  const done = () => mission!.report({ kind: "manual" });
  const watch = explainers[0];
  const watchLength = watch ? explainerLength(watch.seconds) : null;

  return (
    <section
      ref={barRef}
      className={`quest${listed ? " steps-mode" : " whole"}${lu ? " levelup" : finished ? " finished" : ""}${luDone ? " lu-done" : ""}${celebrate ? " celebrate" : ""}`}
      aria-label="Today's mission"
    >
      <AutoRead text={readText} clip={readClip} />
      <div className="quest-main">
        <span className={`eyebrow quest-count${lu ? " lu" : ""}${(lu ? luDone : finished) ? " finished" : ""}`}>
          {lu && (
            <span className="lu-star" aria-hidden="true">
              ★{" "}
            </span>
          )}
          {count}
        </span>
        {coins !== null && (
          <span className="quest-coins" role="status">
            +{coins} coins!
          </span>
        )}
        {listed ? (
          <p className="quest-now" aria-live="polite">
            {shown ? shown.text : lu ? "You beat every level. Keep building!" : "Every step is done. Nice work!"}
          </p>
        ) : (
          <p className="quest-now quest-whole">{assignment.instructions}</p>
        )}
        {shown && <Narrate text={shown.text} clip={`${assignment.id}/${shown.id}`} label={lu ? "Read this level-up aloud" : "Read this step aloud"} />}
        {!listed && <Narrate text={assignment.instructions} clip={`${assignment.id}/mission`} label="Read the mission aloud" />}
        {shown?.done.kind === "manual" && (
          <button type="button" className="primary small quest-done" onClick={done}>
            Done
          </button>
        )}
        {!listed && (
          <button ref={buttonRef} type="button" className="linklike quest-readall" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen(!open)}>
            Read all
          </button>
        )}
      </div>
      <div className="quest-end">
        {lu && (
          <ol className="quest-pips lu-pips" aria-label="Level-ups">
            {levelUps.map((s, i) => {
              const isDone = mission!.done.includes(s.id);
              const isNow = s === luNow;
              return (
                <li
                  key={s.id}
                  className={`pip star${isDone ? " is-done" : ""}${isNow ? " is-now" : ""}`}
                  aria-label={`Level up ${i + 1}: ${isDone ? "done" : isNow ? "now" : "to do"}`}
                  title={`Level up ${i + 1}: ${s.text}`}
                  aria-current={isNow ? "step" : undefined}
                  data-step={s.id}
                >
                  <span aria-hidden="true">★</span>
                </li>
              );
            })}
          </ol>
        )}
        {steps && !lu && (
          <ol className="quest-pips" aria-label="Steps">
            {steps.map((s, i) => {
              const isDone = mission!.done.includes(s.id);
              const isNow = s === current;
              return (
                <li
                  key={s.id}
                  className={`pip${isDone ? " is-done" : ""}${isNow ? " is-now" : ""}`}
                  aria-label={`Step ${i + 1}: ${isDone ? "done" : isNow ? "now" : "to do"}`}
                  title={`Step ${i + 1}: ${s.text}`}
                  aria-current={isNow ? "step" : undefined}
                  data-step={s.id}
                >
                  <span aria-hidden="true">{isDone ? "✓" : i + 1}</span>
                </li>
              );
            })}
          </ol>
        )}
        {listed && (
          <button ref={buttonRef} type="button" className="outline small quest-all" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen(!open)}>
            All steps <span aria-hidden="true">{open ? "▴" : "▾"}</span>
          </button>
        )}
        {skippable && !lu && (
          <button type="button" className="linklike small mission-skip" onClick={() => mission!.setSkipped(!mission!.skipped)}>
            {mission!.skipped ? "Show the steps" : "Skip steps"}
          </button>
        )}
        {watch && (
          <button
            type="button"
            className="quest-watch"
            aria-label={`Watch: ${watch.title}${watchLength ? `, ${watchLength}` : ""}`}
            title={`Watch: ${watch.title}${watchLength ? ` · ${watchLength}` : ""}${explainers.length > 1 ? ` (and ${explainers.length - 1} more under All steps)` : ""}`}
            onClick={() => setPlaying(watch)}
          >
            <span className="watch-play" aria-hidden="true">
              <i />
            </span>
            <span className="quest-watch-label" aria-hidden="true">
              Watch
            </span>
          </button>
        )}
        {teacher && <LessonPlanButton assignmentId={assignment.id} lesson={assignment.lesson} className="mission-plan" />}
      </div>

      {open && (
        <div ref={panelRef} id={panelId} className="quest-panel" role="region" aria-label={listed ? "All steps" : "Today's mission"} tabIndex={-1}>
          <div className="quest-panel-head">
            <h2 className="eyebrow">Today's mission</h2>
            {steps && <span className={`mission-count${finished ? " finished" : ""}`}>{stepCount}</span>}
            <button
              type="button"
              className="icon-close"
              aria-label="Close (Esc)"
              title="Close (Esc)"
              onClick={() => {
                setOpen(false);
                buttonRef.current?.focus();
              }}
            >
              ✕
            </button>
          </div>
          {steps && lu && finished ? (
            // On to the level-ups: the finished steps fold to one line so the panel fits a Chromebook.
            <p className="lu-stepsdone">
              <span aria-hidden="true">✓</span> All {total} steps done
            </p>
          ) : steps ? (
            <ol className="mission-steps" aria-label="Steps">
              {steps.map((s, i) => {
                const isDone = mission!.done.includes(s.id);
                const isNow = s === current;
                return (
                  <li key={s.id} className={`mstep${isDone ? " is-done" : ""}${isNow ? " is-now" : ""}`} aria-current={isNow ? "step" : undefined} data-step={s.id}>
                    <span className="mstep-box" aria-hidden="true">
                      {isDone ? "✓" : i + 1}
                    </span>
                    <span className="mstep-text">
                      {s.text}
                      <span className="sr-only">{isDone ? " (done)" : isNow ? " (now)" : ""}</span>
                    </span>
                    {isNow && <Narrate text={s.text} clip={`${assignment.id}/${s.id}`} label="Read this step aloud" />}
                    {isNow && s.done.kind === "manual" && (
                      <button type="button" className="outline small mstep-done" onClick={done}>
                        Done
                      </button>
                    )}
                  </li>
                );
              })}
            </ol>
          ) : (
            <div className="quest-full">
              <p className="mission-text">{assignment.instructions}</p>
              <Narrate text={assignment.instructions} clip={`${assignment.id}/mission`} label="Read the mission aloud" />
            </div>
          )}
          {skippable && !(lu && finished) && (
            <button type="button" className="linklike small mission-skip" onClick={() => mission!.setSkipped(!mission!.skipped)}>
              {mission!.skipped ? "Show the steps" : "Skip steps"}
            </button>
          )}
          {levelUps.length > 0 && (
            <section className="lu-section" aria-label="Level up">
              <div className="quest-panel-head">
                <h3 className="eyebrow lu-head">
                  <span aria-hidden="true">★ </span>Level up
                </h3>
                <span className={`mission-count${luDone ? " finished" : ""}`}>{lu ? luCount : "After the steps"}</span>
              </div>
              <ol className="mission-steps" aria-label="Level-ups">
                {levelUps.map((s) => {
                  const isDone = mission!.done.includes(s.id);
                  const isNow = s === luNow;
                  return (
                    <li key={s.id} className={`mstep${isDone ? " is-done" : ""}${isNow ? " is-now" : ""}`} aria-current={isNow ? "step" : undefined} data-step={s.id}>
                      <span className="mstep-box lu-box" aria-hidden="true">
                        {isDone ? "✓" : "★"}
                      </span>
                      <span className="mstep-text">
                        {s.text}
                        <span className="sr-only">{isDone ? " (done)" : isNow ? " (now)" : ""}</span>
                      </span>
                      {isNow && <Narrate text={s.text} clip={`${assignment.id}/${s.id}`} label="Read this level-up aloud" />}
                      {isNow && s.done.kind === "manual" && (
                        <button type="button" className="outline small mstep-done" onClick={done}>
                          Done
                        </button>
                      )}
                    </li>
                  );
                })}
              </ol>
              <p className="lu-note">Each one earns {LEVEL_UP_COINS} helper coins. It doesn't change your grade.</p>
            </section>
          )}
          {explainers.map((x, i) => (
            <WatchTile key={x.id} explainer={x} first={i === 0} onPlay={() => setPlaying(x)} />
          ))}
        </div>
      )}
      {/* On document.body: the bar is a size container, which would trap a fixed-position player. */}
      {playing && createPortal(<ExplainerPlayer explainer={playing} onClose={() => setPlaying(null)} />, document.body)}
    </section>
  );
}
